/**
  * @class msysListener
  * @description
  * Top-level base class for Mosy services, servrs and modules.
  * It implements asynchronous messaging between components,
  * provides core methods for managing application lifecycle including reading initial configuration.
  *
  * This class is not intended for direct instatiation.
  * This class is directly extended by mmodModule, msvcService and msvrServer, which extends it for
  * respective types of Mosy components. Normally you should inherit from these three classes (or from classes derived from them).
  *
  * msysListener has two important class static variables, _listeners and _queues.
  * _listeners contains list of registered listeners. Component registers as listener through constructor of msysListener,
  * which registers object of given class using either provided listener_id or if missing, using name of the object's class as default.
  * ID of listener must be unique. ID can be changed later, which has similar effect as exiting original listener and loading new one.
  * Listener's ID is used to uniquelly identify the listener throughout local system.
  *
  * _queues contains list of existing queues.
  *
  */
import RequestError from 'msysRequestError'

const before_loaded_us = unow()
const ms = require('msysMessage')
require('msysCommon')

const logger = (typeof window === 'object' ? window : global)?.LOGGER_WRAPPER === 'main' ? require('../msysLogger/logger_wrapper_main.js') : require('../msysLogger/logger_wrapper.js')
logger.debug('system', 'BEFORELOAD LOADED', before_loaded_us)

if(typeof process === 'object') {
  // server side error handling for uncaught exceptions
  // for workflow scripts errors, log it but don't let server crash
  process.on('unhandledRejection', (err) => {
    if(/eval at addScripts/.test(err?.stack)) {
      const txt = err?.stack ? String(err.stack??'').replace(/\n\s+/g, '; ') : String(err)
      logger.error('system', 'uncaught workflow script exception: '+txt)
    }else {
      console.error_orig('UNCAUGHT ERROR:', err.stack)
      process.removeAllListeners('uncaughtException')
      process.exit(1)
    }
  })

  process.on('uncaughtException', function(err) {
    if(/eval at addScripts/.test(err?.stack)) {
      const txt = err?.stack ? String(err.stack??'').replace(/\n\s+/g, '; ') : String(err)
      logger.error('system', 'uncaught workflow script exception: '+txt)
    }else {
      console.error_orig('UNCAUGHT ERROR:', err.stack)
      process.removeAllListeners('uncaughtException')
      process.exit(1)
    }
  })
}

let mosy_loaded_dt = null
let mosy_started_dt = null
const _listeners = {}
const _queues = {}
const _locks = {}
const _aborts = {}
const mcrypt = require('msysCrypt')
const schema_types = require('../../MosyMessageTypes.json')
const schema_actions = require('../../MosyMessageActions.json')
for(const action in schema_actions) {
  if(/"special_type":/.test(JSON.stringify(schema_actions[action]))) schema_actions[action].special_type = true
  if(/^mm[A-Z]\w*$/.test(action)) {
    const def = schema_actions[action]
    if(def.type !== 'object') continue
    if(!def.properties) def.properties = {}
    def.properties.event_dt = { type: 'integer', description: 'time of event in microseconds' }
    def.properties.orig_dt = { type: 'integer', description: 'original time of event in microseconds when timeshift applies' }
    def.properties._meta = { type: 'object', description: 'metadata that are not recorded nor transported' }
  }
}
const Ajv = require('ajv')
const AjvMergePatch = require('ajv-merge-patch')
const action_validators = {}
const BASE_CONFIG_SCHEMA = {
  disabled: { type: 'boolean', description: 'if true, service/module will not be started' },
  delayed: { type: 'integer', description: 'delay for start of the listener in milliseconds', minimum: 0, default: 0 },
  reply_timeout: { type: 'number', description: 'default timeout for getting reply to request message in milliseconds', default: 10000 },
  ttl: { type: 'integer', description: 'default time to live for offline messages in seconds', default: 60*60*24*30 },
}
const SEQUENTIAL_START = false
const SEQUENTIAL_DELAY = 0
const LONG_ACTION_WARNING = 50000
const LONG_START_ERROR = 5000

const instance_id = mcrypt.oid()
let starting_listener
const numeric_listeners = []

const ajv = Ajv({
  v5: true,
  useDefaults: true,
  coerceTypes: true,
  beautify: false,
  removeAdditional: 'all',
  extendRefs: true,
})

ajv.addKeyword('special', {
  modifying: true,
  schema: true,
  errors: false,
  validate: function (schema, data, parentSchema, path, parent, property) {
    if(schema==='ArrayBuffer') {
      const out =
        (typeof Buffer==='function' && data instanceof Buffer) ? data.buffer.slice(data.byteOffset, data.byteOffset+data.length) :
          (data instanceof ArrayBuffer) ? data :
            (data && typeof data==='object' && data.buffer instanceof ArrayBuffer) ? data.buffer :
              null
      if(out===null) {
        return false
      }
      parent[property] = out
      return true
    }
    return true
  },
})

AjvMergePatch(ajv)
ajv.addSchema(schema_types)

export default class msysListener {
////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates an instance of the class.
  * @param {string} [id] - ID under which the object will listen for messages
  * @param {string} [cfg] - object initial configuration
  * @param {string} [cfg_schema] - additional schema properties from derived classes
  * @param {string} [dependencies] - additional dependencies from derived classes
  */
  constructor(id, cfg, cfg_schema, dependencies) {
    this._stopwatch = {}
    this._listeners = _listeners
    this._logger = logger
    this._queues = _queues
    this._waitingFor = []
    this._requests = {}
    this._daemons = []
    this._schema_types = schema_types
    this._schema_actions = schema_actions
    if(String(id)==='0') {
      if(numeric_listeners.length===0) {
        id = 1
        numeric_listeners.push(id)
      }else if(numeric_listeners[numeric_listeners.length-1]===numeric_listeners.length) {
        id = numeric_listeners.length+1
        numeric_listeners.push(id)
      }else{
        for(let i=1; i<=numeric_listeners.length; i++) {
          if(numeric_listeners[i-1]!==i) {
            id = i
            numeric_listeners.splice(i-1, 0, id)
            break
          }
        }
      }
      id = String(id)
    }else if(!id) {
      id = this.constructor.name
    }else{
      id = String(id)
    }
    this.instance_id = instance_id

    dependencies = _.union(['msysLogger'], dependencies)
    dependencies = dependencies.filter(dep=>dep!==id)

    cfg_schema = _.extend({}, BASE_CONFIG_SCHEMA, cfg_schema)

    // store configuration schema and dependencies for future use
    this.dependencies = dependencies.sort()
    this.config_schema = {
      type: 'object',
      properties: cfg_schema,
    }

    // setup our listener ID
    this.setId(id)
    if(!this.listener_id) {
      return
    }

    // setup config property
    this.config = _.isObjectLike(cfg) ? cfg : {}

    // validate configuration; removes undefined properties and coerces types of known properties
    // if configuration is incorrectable, validate {}, which appends defaults for properties
    if(!ajv.validate(this.config_schema, this.config)) {
      this.error('Invalid configuration for '+id, ajv.errors)
      this.config = {}
      ajv.validate(this.config_schema, this.config)
    }

    if(this.config.disabled) {
      // this listener is configured to not start
      _listeners[this.listener_id].status = 'exited'
      this.publish('#ALL', 'mmExited')
      return
    }

    // setup check on long start of listener
    this.long_start_checker = setTimeout(()=>{
      const numeric = String(parseInt(this.listener_id))===String(this.listener_id)
      const status = _listeners[this.listener_id] ? _listeners[this.listener_id].status : numeric ? 'exited' : ''
      if(status!=='started' && status!=='exited') { this.error(`Listener not started after ${LONG_START_ERROR} ms`) }
    }, this.config.delayed + LONG_START_ERROR)

    // setting up the start of listener
    setTimeout(()=>{
      this.waitFor(this.dependencies, 'mmStart')
    }, this.config.delayed)
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  *
  */
  setId(id, newStatus) {
    if(!/^\w+$/.test(id)) {
      this.error('Cannot instantiate listener without ID')
      return
    }
    if(_listeners[id] && _listeners[id].ref!==this && _listeners[id].status!=='exited') {
      console.error('Duplicit listener ID: '+id)
      this.id = ''
      return
    }

    const oldStatus = _listeners[this.listener_id] ? _listeners[this.listener_id].status : ''
    newStatus = String(newStatus??'') || oldStatus || 'loaded'

    if(String(id) === String(this.listener_id??'')) {
      // ID did not really changed; just update listener's status
      _listeners[id].status = newStatus
      if(oldStatus!=='started' && newStatus==='started') { this.publish('#ALLWITHME', 'mmStarted') } else if(oldStatus!=='exited' && newStatus==='exited') { this.publish('#ALL', 'mmExited') }
    }else {
      // ID changed; delete old, create new listener record and notify others
      if(oldStatus && oldStatus!=='exited') { this.publish('#ALL', 'mmExited') }
      delete _listeners[this.listener_id]
      this.listener_id = id
      _listeners[id] = {
        ref: this,
        status: newStatus,
      }
      if(newStatus==='started') { this.publish('#ALLWITHME', 'mmStarted') } else if(newStatus==='exited') { this.publish('#ALL', 'mmExited') }
    }
  } // setId

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Called when configuration and dependencies load.
  * Shall be overwritten by subclasses.
  * Inherited classes shall always call `super.mmStart(sender, params)` when they
  * successfully finish initialization.
  * In case of initialization failure, listener shall call `super.mmExit(sender, params)` instead.
  */
  mmStart(sender) {
    if(sender && String(sender.listener_id)!==String(this.listener_id)) return
    _listeners[this.listener_id].status = 'started'
    if(this.long_start_checker) {
      clearTimeout(this.long_start_checker)
      delete this.long_start_checker
    }
    this.listener_started_dt = unow()
    const start_duration = this.listener_started_dt - this.listener_start_dt
    const start_delay_duration = this.listener_start_dt - this.listener_start_dt1
    this.debug(`Instance of ${this.constructor.name} started after ${(start_duration/1000).toFixed(3)} ms with delay ${(start_delay_duration/1000).toFixed(3)} ms`)
    if(SEQUENTIAL_START) {
      if(String(starting_listener??'')===String(this.listener_id??'')) {
        starting_listener = null
      }else{
        this.error('different starting_listener: '+starting_listener)
      }
    }
    this.publish('#ALLWITHME', 'mmStarted', { duration: start_duration })
    this._checkStartedAll()
  } // started

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Called when listener is required to stop.
  * Shall be overwritten by subclasses.
  * Inherited classes shall always call `super.mmExit(sender, params)` when they
  * finish exit cleanup.
  * - stops daemons
  * - unsubscribes from all queues
  * - published mmExited
  * - if this is last listener, stops the application
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.delete] - if true, will be deleted from list of listeners
  * @param {string} [params.error] - reason for exit
  * @param {number} [params.exit_code] - exit code for process.exit() in NW.js kiosk mode (spracovava msvcAppControl override, interpretuje watchdog: 100=update, 110=config, 120=remote restart, 130=remote exit, 170=logoff, 171=device restart, 172=device shutdown, 175=security, 200=service)
  */
  mmExit(sender, params) {
    if(!_listeners[this.listener_id]) return

    // stop daemons
    while(this._daemons.length>0) {
      const daemon_def = this._daemons.shift()
      try { clearInterval(daemon_def[1]) }catch(e) {}
    }

    this.unsubscribe()

    _listeners[this.listener_id].status = 'exited'
    if(this.long_start_checker) {
      clearTimeout(this.long_start_checker)
      delete this.long_start_checker
    }

    const numeric = String(parseInt(this.listener_id))===String(this.listener_id)
    if(numeric || params?.delete) {
      // numeric listener shall be removed from list of numeric listeners
      const idx = numeric_listeners.indexOf(Number(this.listener_id))
      if(idx>=0) numeric_listeners.splice(idx, 1)
      delete _listeners[this.listener_id]
    }
    const running = Object.keys(_listeners).filter((id)=>{ return _listeners[id] && _listeners[id].status!=='exited' })
    if(running.length === 0) {
      try{
        logger.terminate()
      }catch(e) {
        // ignore
      }
      if(typeof window !== 'undefined') {
        this.closeGUIApplication()
      } else {
        process.exit()
      }
    }

    if(!numeric) this.debug('listener exited after '+((unow()-this.listener_start_dt)/1000).toFixed(3)+' ms')
    if(SEQUENTIAL_START) {
      if(String(starting_listener??'')===String(this.listener_id??'')) {
        starting_listener = null
      }else{
        this.error('different starting_listener: '+starting_listener)
      }
    }
    this.publish('#ALL', 'mmExited')
    this._checkStartedAll()
  } // mmExit

  closeGUIApplication() {
    if(typeof window !== 'object') {
      return
    }
    if(typeof window.nw !== 'undefined') {
      try {
        window.close(true)
      } catch (error) {
        this.error(`Could not close GUI app: ${error}`)
      }
    } else {
      // we are in browser, let's reload
      window.location.reload()
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @param {string|object} target - target of message specified by string or already split
  * @description
  * Transforms address string (sender or target) into hash of components.
  * If hash is provided, verifies its components.
  * Returns null if unsuccessful, otherwise returns object with these keys:
  * - app_id: ID of destination application
  * - scope: ':' for server-client network, '@' for peering network, '' for local listeners
  * - listener_id: either single listener (just letters) or queue (# followed by letters)
  * - request_id: sender-specified internal ID that must be used when replying to the message
  */
  address2hash(str) {
    const hash = ms.address2hash(str)
    if(!hash) this.error('Invalid address: ', str)
    return hash
  } // address2hash

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @param {string|object} target - target of message specified by string or already split
  * @description
  * Transforms address hash into string.
  * If string is provided, verifies the string and return it.
  * Returns null if unsuccessful, otherwise returns address string.
  */
  address2string(hash) {
    const str = ms.address2string(hash)
    if(!str) this.error('Invalid address: ', hash)
    return str
  } // address2string

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @param {string} action - name of action to verify
  * @param {object} params - parameters to verify
  * @description
  * Verifies parameters agains JSON schema for given action
  */
  _validateParams(action, params) {
    let validator = action_validators[action]
    if(typeof validator !== 'function') {
      const schema = schema_actions[action]
      if(typeof schema==='object' && schema.type) { validator = action_validators[action] = ajv.compile(schema) }
    }
    if(typeof validator !== 'function') return ['Invalid action: '+action]
    const valid = validator(params)
    let errors = valid ? [] : validator.errors
    errors = errors.map(err=>err.dataPath+' '+String(err.message??''))
    return valid ? null : errors
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send message to target
  * @param {string|object} target - recipient of the message; see details on format on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  */
  publish(target, action, params) {
    this.publishAs(null, target, action, params)
  } // publish

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send message on behalf of other listener.
  * Used by some system listeners (e.g. sysDomainServer) to simulate messages coming from other sender.
  * @param {string|object} sender - indicated sender of the message; see details on format on _sendAs()
  * @param {string|object} target - recipient of the message; see details on format on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  * Note: can be called also when sender contains complete send string, like SENDER>TARGET.ACTION?PARAMS
  */
  publishAs(sender, target, action, params, nolog) {
    const sender_hash = this.address2hash(sender||this.listener_id||'unknown_listener')
    if(!sender_hash) return this.error('Invalid sender: '+JSON.stringifySafe(sender))
    const target_hash = this.address2hash(target)
    if(!target_hash) return this.error('Invalid target: '+JSON.stringifySafe(target))
    this._sendAs(sender_hash, target_hash, action, params, nolog)
  } // publishAs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send request waiting for reply
  * @param {string|object} target - recipient of the message; see details on format on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} params - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  * @param {object|string|array} options - options for the request/reply mechanism; if string/array is provided, it is treated as options.resolve
  * @param {integer} [options.timeout] - timeout in ms; if not provided, reply_timeout as configured for the listener is used
  * @param {number|array} [options.timeout] - timeout in ms; if not provided, reply_timeout as configured for the listener is used
  * @param {string|string[]} [options.resolve] - one or more actions - if received reply for the request with this action, request promise is resolved
  * @param {string|string[]} [options.reject] - one or more actions - if received reply for the request with this action, request promise is rejected
  * @returns {Promise} promise that satisfies when reply came or fails on timeout/error
  */
  request(target, action, params, options) {
    options = (Array.isArray(options) || typeof (options)==='string') ? { resolve: options } : (options && typeof (options)==='object') ? options : {}
    // generate request id
    const request_id = mcrypt.oid().substr(1)

    let reject, resolve, timeouter

    const resolve_actions = Array.isArray(options.resolve) ? options.resolve : typeof (options.resolve)==='string' ? [options.resolve] : []
    const reject_actions = Array.isArray(options.reject) ? options.reject : options.reject ? ['mmFailure', 'mmUndelivered'] : []
    const done = (action, params, sender, target)=>{
      sender = sender || ms.address2hash(this.listener_id)
      target = target || this.address2hash('#ME$'+request_id)
      params = params && typeof (params)==='object' ? params : {}
      const reply_msg = { sender, target, action, params, options }
      if((resolve_actions.length>0 && resolve_actions.indexOf(action)<0) || reject_actions.indexOf(action)>=0) {
        const err = new RequestError(params?.error, action, params, sender, target)
        err.name = reply_msg.action
        if(reject) reject(err)
        else Promise.reject(err)
      }else{
        if(resolve) resolve(reply_msg)
        else Promise.resolve(reply_msg)
      }
    }

    const error = (action, params, sender, target)=>{
      this.error('Request error:', params ? params.error : undefined)
      done(action, params, sender, target)
    }

    // verify request_id is unique
    if(this._requests[request_id]) return error('mmUndelivered', { error: 'duplicate_request_id', target: this.address2string(target) })

    // analyze target
    const target_hash = this.address2hash(target)
    if(!target_hash) return error('mmUndelivered', { error: 'invalid_target', target: JSON.stringifySafe(target) })

    // prepare sender hash to be sent with request id to allow tracking back when reply came
    const sender_hash = this.address2hash(this.listener_id)
    if(!sender_hash) return error('mmUndelivered', { error: 'invalid_sender' })
    sender_hash.request_id = request_id

    // prepare function that will be called in _listen when message with the same request_id arrives
    this._requests[request_id] = (reply_sender_hash, reply_target_hash, reply_action, reply_params)=>{
      // unregister request callback
      delete this._requests[request_id]

      // remove timeouter
      if(timeouter) {
        clearTimeout(timeouter)
        timeouter = null
      }

      // verify incoming parameters
      if(!_.isObjectLike(reply_sender_hash)) return error('mmUndelivered', { error: 'invalid_reply_sender' })
      if(typeof (reply_params)==='undefined' || reply_params==null) reply_params = {}
      if(typeof (reply_params)!=='object') return error('mmUndelivered', { error: 'invalid_reply_params' })

      // perform done() to resolve (reject) the promise
      done(reply_action, reply_params, reply_sender_hash, reply_target_hash)
    }

    // actually send request to target
    this._sendAs(sender_hash, target_hash, action, params)

    // setup timer that will call mmUndelivered when no reply came
    const timeout_ms = _.natural(options.timeout || this.config.reply_timeout)
    if(timeout_ms>0) {
      timeouter = setTimeout(()=>done('mmUndelivered', { error: 'timeout', target: this.address2string(target_hash) }), timeout_ms)
    }

    const promise = new Promise((_resolve, _reject)=>{
      resolve = _resolve
      reject = _reject
    })

    promise.abort = (error = 'abort')=>{
      done('mmUndelivered', { error, target: this.address2string(target_hash) })
    }

    return promise
  } // request

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Sends mmAction request and returns reply
  * @param {string|object} target - recipient of the message; see details on format on _sendAs()
  * @param {string} action - workflow action to be invoked
  * @param {string} id - ID of workflow object
  * @param {object} params - workflow parameters
  * @param {function} callback(err, result) - callback after finish
  */
  async wfRequest(target, action, id, params, callback) {
    if(callback) {
      try{
        const { params: rparams } = await this.request(target, 'mmAction', { action, id, params }, 'mmObjects')
        callback(null, rparams)
      }catch(e) {
        callback(String(e))
      }
    }else{
      return await this.request(target, 'mmAction', { action, id, params }, 'mmObjects')
    }
  } // wfRequest

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send reply to received request message.
  * Reports warning if target does not have request_id (i.e. it is not real reply, but just publish).
  * @param {string|object} target - recipient of the message; see details on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  */
  reply(target, action, params) {
    const target_hash = this.address2hash(target)
    if(!target_hash) return this.error('Invalid target: '+JSON.stringifySafe(target))
    //  if(!target_hash.request_id) this.warn('Reply without request: '+JSON.stringifySafe(target));
    const sender_hash = this.address2hash(this.listener_id)
    this._sendAs(sender_hash, target_hash, action, params)
  } // request

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send reply if received message was a request
  * @param {string|object} target - recipient of the message; see details on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  */
  replyIfRequest(target, action, params) {
    if(!target) return
    const target_hash = this.address2hash(target)
    if(!target_hash) return this.error('Invalid target: '+JSON.stringifySafe(target))
    if(target_hash.request_id) {
      const sender_hash = this.address2hash(this.listener_id)
      this._sendAs(sender_hash, target_hash, action, params)
    }
  } // replyIfRequest

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Send reply to received request message.
  * Reports warning if target does not have request_id (i.e. it is not real reply, but just publish).
  * @param {string|object} target - recipient of the message; see details on _sendAs()
  * @param {string} action - message type name; must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  */
  replyFailure(target, error, code, quiet) {
    const target_hash = this.address2hash(target)
    if(!target_hash) return this.error('Invalid target: '+JSON.stringifySafe(target))
    if(target_hash.request_id) {
      const sender_hash = this.address2hash(this.listener_id)
      const params = { error }
      if(code) params.code = code
      this._sendAs(sender_hash, target_hash, 'mmFailure', params)
    }else if(!quiet) {
      let txt = String(error)
      if(code) txt += ` (code: ${code})`
      txt += ` for ${this.address2string(target_hash)}`
      this.error(txt)
    }
  } // replyFailure

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Helper method when replying with mmSuccess/mmFailure.
  * - if target has no request_id (i.e. it is not request but publish and is not interested in response), don't do anything.
  * - if success=true, replies to sender with .mmSuccess?
  * - if success=false, replies to sender with .mmFailure?{"error": error}
  * @param {string|object} target - recipient of the message
  * @param {boolean} success - indicates whether mmSuccess or mmFailure shall be sent
  * @param {string} [error] - optional error message for mmFailure
  */
  replyResult(target, success, error) {
    const target_hash = this.address2hash(target)
    if(!target_hash || !target_hash.request_id) return
    const sender_hash = this.address2hash(this.listener_id)
    error = String(error??'')
    error = error==='' ? {} : { error }
    if(success) {
      this._sendAs(sender_hash, target_hash, 'mmSuccess', {})
    }else {
      this._sendAs(sender_hash, target_hash, 'mmFailure', error)
    }
  } // replyResult

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|object} target - recipient of the message; either split into parts or put as single string
  * @param {string} target.app_id - application ID of target
  * @param {string} target.scope - ':' for server, '@' for peering, '' for local
  * @param {string} target.listener_id - listener ID (just letters) or queue ID (# followed by letters)
  * @param {string} target.request_id - internal sender's ID that shall be replied back in a response message
  * @param {string} action - message type name; must be prefixed with `mm` and must be defined in Mosy Message Catalog (MessageCatalog.json)
  * @param {object} [params={}] - must correspond to JSON schema as defined for specified message type in Mosy Message Catalog
  * @param {string} [force_sender] - use specified sender instead of ourselves
  * @description
  * This is the single most important method the class implements. It provides to derived classes
  * method to send asynchronous message to other Mosy component.
  * Message can be sent to either single or multiple recipients as defined by `target` parameter.
  * Target formats:
  * - simple word (e.g. mmodMap, msvcAlpr) specifies single listener
  * - `#ALL` will send message to all other started listeners except the sender
  * - `#ALLWITHME` will send message to all started listeners including the sender
  * - `#ME` will send message to sender
  * - other word prefixed with `#` will send message to all listeners subscribed to queue with the name after # (including sender if subscribed there)
  * - `@` prefix indicates the message shall be send within all computers within local peering network; after `@` may follow either listener name or `#` with queue name
  * - ':' prefix indicates the message shall be sent to server; after `:` may follow either server-side listener name or `#` with server-side queue name
  *
  * Message types are defined in Mosy Message Catalog.
  * This is JSON schema of allowed message types and allowed structure of their parameters.
  * Messages not obeying the specification will not be delivered to handlers (this is implemented in @see {@link _listen} method).
  * The catalog is stored in MessageCatalog.json file.
  *
  * Note that listener **can** send messages even if not in `started` state (but can't receive messages).
  */
  _sendToQueueSubscribers(queue_name, skip_sender, sender_hash, target_hash, action, params) {
    let sender, target
    const queue = _queues[queue_name]
    if(!queue) return
    for(const subscriber of queue) {
      const subscriber_hash = this.address2hash(subscriber.address)
      if(skip_sender && String(subscriber_hash.listener_id??'') === String(sender_hash.listener_id??'')) continue
      let params_copy = params
      if(typeof (subscriber.filter)==='function') {
        try{
          if(!sender) sender = ms.address2string(sender_hash)
          if(!target) target = ms.address2string(target_hash)
          const event_dt = params ? params.event_dt : unow()
          params_copy = subscriber.filter(sender, target, action, params)
          if(!params_copy) continue
          params_copy.event_dt = event_dt
        }catch(e) {
          this.error('Error send to queue subscriber '+sender+': '+e)
          continue
        }
      }
      if(queue_name==='#MSG') {
        if(params_copy && '_meta' in params_copy) {
          if(params_copy === params) params_copy = { ...params }
          delete params_copy._meta
        }
        this._sendAs(
          { listener_id: 'msysStreamer' },
          subscriber_hash,
          'mmMessage',
          {
            sender: sender_hash,
            target: target_hash,
            action,
            params: params_copy,
          },
          true,
          target_hash,
        )
      }else {
        this._sendAs(sender_hash, subscriber_hash, action, params_copy, true, target_hash)
      }
    }
  } // _sendToQueueSubscribers

  _sendAs(sender_hash, target_hash, action, params, nolog, reported_target_hash) {
    reported_target_hash = reported_target_hash || target_hash
    if(!params || typeof params!=='object') params = {}
    params.event_dt = params.event_dt || unow()
    action = String(action??'')
    if(!sender_hash) return this.error(`No sender when sending message: ${action}`)
    if(!target_hash) return this.error(`No target when sending message: ${action}`)
    if(!action) return this.error('No action when sending message')
    if(!nolog) {
      // log all messages except those published to #LOG channel
      if(reported_target_hash.listener_id!=='#LOG') {
        // do not send mmH264 payload to logger
        logger.message(sender_hash, target_hash, action, action==='mmH264' ? { ...params, payload: params.payload?.length+' bytes' } : params)
      }

      // send message to all listeners subscribed to #MSG queue
      if(target_hash.listener_id !== '#MSG') {
        this._sendToQueueSubscribers('#MSG', false, sender_hash, target_hash, action, params)
      }
    }

    const abort = (error, error_msg) => {
      this.error(error_msg || error)
      if(sender_hash.request_id) {
        this._sendAs(_.omit(sender_hash, ['request_id']), this.address2hash('#ME$'+sender_hash.request_id), 'mmUndelivered', { error, target: this.address2string(target_hash) })
      }
    }

    // validate parameters
    const original_params = typeof (params)==='object' ? params : {}
    params = this.cloneParams(action, original_params)
    let payload
    if(action==='mmH264') {
      // do not send mmH264 payload data to validator
      payload = params?.payload
      if(payload) params.payload = new Uint8Array(0)
    }
    try{
      const errors = this._validateParams(action, params)
      if(errors) {
        const error_msg = 'Message validation failed for '+this.address2string(sender_hash)+'>'+this.address2string(target_hash)+'.'+action+': '+errors.join(';')
        return abort('message validation failed', error_msg)
      }

      let removed_keys = _.difference(_.keys(original_params), _.keys(params))
      removed_keys = removed_keys.filter(key=>typeof (original_params[key])!=='undefined')
      if(removed_keys.length>0) { this.warn(`[${_.debugtime()}] Validation removed params for ${action}: `+removed_keys.map(k=>`${k}=${JSON.stringifySafe(original_params[k])}`).join(',')) }
    }catch(e) {
      this.error(`[${_.debugtime()}] Problem with schema for ${action}: `+e)
    }
    if(action==='mmH264' && payload!==undefined) params.payload = payload

    // remote scope
    if(target_hash.scope===':') {
      if(MOSY_SERVER) {
        // send to specific client
        this._sendToLocalListener(sender_hash, reported_target_hash, 'msvrServer', '', 'mmSendToClient', { target: target_hash, action, params })
      }else {
        // send to server
        this._sendToLocalListener(sender_hash, reported_target_hash, 'msysDomainClient', '', 'mmSendToServer', { target: target_hash, action, params })
      }
      return
    }

    // peering scope
    if(target_hash.scope==='@') {
      if(sender_hash.request_id && !target_hash.app_id) {
        return abort('Request to all peers not allowed')
      }

      // send to peering
      // try to send through msysPeeringServer, msysPeeringClient or at least send to ourselves if not peering service included
      if(_listeners.msysPeeringServer) {
        this._sendToLocalListener(sender_hash, reported_target_hash, 'msysPeeringServer', '', 'mmSendToPeers', { target: target_hash, action, params })
      }else if(_listeners.msysPeeringClient) {
        this._sendToLocalListener(sender_hash, reported_target_hash, 'msysPeeringClient', '', 'mmSendToPeers', { target: target_hash, action, params })
      } else if(!target_hash.app_id || String(target_hash.app_id??'')===String(this.app_id??'')) {
        // fallback to send it locally if it addresses our own app or all
        this._sendAs(sender_hash, _.omit(target_hash, ['scope', 'app_id']), action, params, true, reported_target_hash)
      }

      // directly connected Mosy apps behave like peers, but can be addressed only directly not through 'to all peers' notation
      if(target_hash.app_id) {
        if(_listeners.msysDirectServer) { this._sendToLocalListener(sender_hash, reported_target_hash, 'msysDirectServer', '', 'mmSendToDirects', { target: target_hash, action, params }) }
        if(_listeners.msysDirectClient) { this._sendToLocalListener(sender_hash, reported_target_hash, 'msysDirectClient', '', 'mmSendToDirects', { target: target_hash, action, params }) }
      }
      return
    }

    // tab scope
    if(target_hash.scope==='~') {
      this._sendToLocalListener(sender_hash, reported_target_hash, 'msysTabClient', '', 'mmSendToTabs', { target: target_hash, action, params })
      return
    }

    // send to local queue/listener; note that if sending to peers, it means also sending to us too
    const sender_listener_id = String(sender_hash.listener_id??'')
    if(target_hash.listener_id==='#ME') {
      this._sendToLocalListener(sender_hash, reported_target_hash, sender_hash.listener_id, target_hash.request_id, action, params)
    }else if(target_hash.listener_id==='#ALL') {
      for(const [listener_id, listener] of Object.entries(_listeners)) {
        if(String(listener_id??'') !== sender_listener_id && listener?.status==='started') {
          this._sendToLocalListener(sender_hash, reported_target_hash, listener_id, target_hash.request_id, action, params)
        }
      }
    }else if(target_hash.listener_id==='#ALLWITHME') {
      for(const [listener_id, listener] of Object.entries(_listeners)) {
        if(listener?.status==='started' || listener_id===sender_listener_id || action==='mmStarted' || action==='mmWindow') {
          this._sendToLocalListener(sender_hash, reported_target_hash, listener_id, target_hash.request_id, action, params)
        }
      }
    }else if(target_hash.listener_id.substr(0, 1)==='#') {
      this._sendToQueueSubscribers(target_hash.listener_id, false, sender_hash, target_hash, action, params)
    }else {
      // send to individual listener
      if(_listeners[target_hash.listener_id]) {
        this._sendToLocalListener(sender_hash, reported_target_hash, target_hash.listener_id, target_hash.request_id, action, params)
      }else{
        return abort('no listener', 'no listener '+target_hash.listener_id)
      }
    }
  } // _sendAs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @param {object} sender_hash - sender of the message
  * @param {string} listener_id - ID of target listener (must be individual listener only)
  * @param {string} action - message type
  * @param {object} [params] - parameters for the message
  * @description
  * Internal helper method that actually invokes _listen method of the individual listener.
  */
  _sendToLocalListener(sender_hash, target_hash, listener_id, request_id, action, params) {
    const listener_wrap = _listeners[listener_id]
    if(!listener_wrap) return
    const status = listener_wrap.status
    const listener = listener_wrap.ref

    // listener exited
    if(status==='exited') {
      return
    }

    // listener is not started; allow
    if(!(status==='started' || request_id || action==='mmStart' || action==='mmStarted' || action==='mmWindow')) {
      if(sender_hash.listener_id!=='msysWM' && action!=='mmSubscribed' && action!=='mmMessage') {
        if(!['mmValueChanged', 'mmFormStatus', 'mmListSelected', 'mmTabSelected'].includes(action) || listener_wrap.status!=='loaded') {
          this.warn('Sending '+action+' to not started ('+listener_wrap.status+') listener', listener_id)
        }
      }
      return
    }

    // listener does not handle this type of message
    if(!(request_id || action==='mmStarted' || action==='mmWindow' || typeof listener[action]==='function' || typeof listener.defaultHandler==='function')) {
      return
    }

    if(typeof (params)==='undefined' || params==null) params = {}
    if(typeof (params)!=='object') return // accept only object as parameters

    const copied_params = this.cloneParams(action, params)
    setTimeout(()=>{
      try{
        const dt0 = unow()
        listener._listen(sender_hash, target_hash, request_id, action, copied_params||{})
        const duration = unow() - dt0
        if(duration>LONG_ACTION_WARNING) this.warn(`long action: ${this.address2string(sender_hash)}>${listener_id}.${action} took ${(duration/1000).toFixed(3)}ms`)
      }catch(e) {
        this.error(`Error calling listen ${listener?.constructor?.name} ${listener_id}.${action}: ${e.message}`)
      }
    }, 0)
  } // _sendToLocalListener

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @param {object} sender_hash - sender of the message
  * @param {object} target_hash - target of the message
  * @param {string} action
  * @param {string} action
  * @param {object} [params={}]
  * @description
  * This private method implements handling of incoming messages and invoking
  * appropriate handler method. It should not be used nor changed by derived
  * classes unless very special cases (e.g. msysLogger which want generic handling of all messages)
  * If listener wants to universally handle any message type, it should rather implement this.defaultHandler()
  */
  _listen(sender_hash, target_hash, request_id, action, params) {
    // special handling of 'started' message; check if we are waiting
    if(action==='mmStarted') {
      this._checkWaitFor()
    }

    if(request_id) {
      // handling of reply to a request message
      const request_done = this._requests[request_id]
      if(!request_done) return this.warn(`Reply without request: ${this.listener_id}$${request_id}.${action}`)
      try{
        request_done(sender_hash, target_hash, action, params)
      }catch(e) {
        this.error('Invalid request_done', e)
      }
    }else{
      // don't send messages to non-started listeners (except mmStart and mmWindow)
      if(_listeners[this.listener_id]?.status !== 'started' && action!=='mmStart' && action!=='mmWindow') {
        return
      }

      try{
        // skip call if message handler is not defined
        if(typeof (this[action])==='function') {
          if(action==='mmStart' && String(sender_hash.listener_id??'')===String(this.listener_id??'') && !this.listener_start_dt) {
            this.listener_start_dt = unow()
          // this.debug('Starting listener with start delay '+(this.listener_start_dt-this.listener_start_dt1)+' ms')
          }
          this[action](sender_hash, params, target_hash)
        }else if(typeof (this.defaultHandler)==='function') {
          this.defaultHandler(action, sender_hash, params, target_hash)
        }
      }catch(e) {
        this.error(`Error calling ${this.listener_id}.${action}: `+e.name+': '+e.message+' - '+e.stack)
      }
    }
  } // _listen

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|string[]} queue(s) - either queue name (prefixed with #) or array of such queues
  * @description
  * Subscribes the listener to given queue(s).
  * After subscription (and while in state `started`) the listener will receive all messages sent to this queue(s).
  */
  subscribeAs(address, queues, filter) {
    const desired_queues = Array.isArray(queues) ? queues : [queues]
    for(const queue of desired_queues) {
      if(!/^#\w+$/.test(queue)) {
        this.error(`${address} tries to subscribe to invalid queue: ${queue}`)
        continue
      }
      if(!_queues[queue]) _queues[queue] = []
      let idx = _queues[queue].findIndex(subscriber=>String(subscriber.address??'')===String(address??''))
      if(idx<0) idx = _queues[queue].length
      filter = ms.createFilterFunction(filter, true)
      _queues[queue][idx] = { address, filter }
      this.publishAs(address, queue, 'mmSubscribed', { queue })
    }
  } // subscribeAs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|string[]} queue(s) - either queue name (prefixed with #) or array of such queues
  * @description
  * Subscribes the listener to given queue(s).
  * After subscription (and while in state `started`) the listener will receive all messages sent to this queue(s).
  */
  subscribe(queues, filter) {
    this.subscribeAs(this.listener_id, queues, filter)
  } // subscribe

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|string[]|null} queue(s) - either queue name (prefixed with #) or array of such queues; if empty, unsubscribes from all queues
  * @description
  * Unsubscribes the listener from given queue(s).
  */
  unsubscribeAs(address, queues) {
    const desired_queues = Array.isArray(queues) ? queues : queues ? [queues] : Object.keys(_queues)
    for(const queue of desired_queues) {
      if(!/^#\w+$/.test(queue)) {
        this.error(`${address} tries to unsubscribe from invalid queue: ${queue}`)
        continue
      }
      if(!_queues[queue]) continue
      const idx = _queues[queue].findIndex(subscriber=>String(subscriber.address??'')===String(address??''))
      if(idx>=0) {
        _queues[queue].splice(idx, 1)
        this.publishAs(address, queue, 'mmUnsubscribed', { queue })
        // Clean up empty queue to prevent memory leak
        if(_queues[queue].length === 0) {
          delete _queues[queue]
        }
      }
    }
  } // unsubscribeAs

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|string[]|null} queue(s) - either queue name (prefixed with #) or array of such queues; if empty, unsubscribes from all queues
  * @description
  * Unsubscribes the listener from given queue(s).
  * After subscription (and while in state `started`) the listener will receive all messages sent to this queue(s).
  */
  unsubscribe(queues) {
    this.unsubscribeAs(this.listener_id, queues)
  } // unsubscribe

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Unsubscribes all listeners matching the regexp from all queues where they are subscribed.
  */
  unsubscribeAllMatching(regexp_string) {
    let re
    try{
      re = new RegExp(regexp_string)
    }catch(e) {
      this.error(`Invalid regexp in ${this.listener_id}.unsubscribeAllMatching: ${regexp_string}`)
      return
    }
    for(const queue in _queues) {
      _queues[queue] = _queues[queue].filter(subscriber => {
        if(re.test(subscriber.address)) {
          this.publishAs(subscriber.address, queue, 'mmUnsubscribed', { queue })
          return false
        }else{
          return true
        }
      })
    }
  } // unsubscribeAllMatching

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @param {string|string[]} listeners - ID of listener (or list of such IDs) to wait for
  * @param {string} action - message to be sent
  * @param {object} params - params to be sent with message
  * @description
  * This method is usually used while starting the listener to wait until some other listeners
  * finish their starting by either `started` or `exited` state. It is a great wait to ensure
  * dependencies are finished during listener_id's initialization.
  * Callback is called with 1 parameter set to `true` if ALL listeners were loaded successfully, `false` otherwise.
  */
  waitFor(listeners, action, params) {
    if(!Array.isArray(listeners) || String(action??'')==='') { return this.error('Invalid waitFor call') }
    listeners = listeners.filter(listener_id=>{ return typeof (listener_id)==='string' && String(listener_id??'')!==String(this.listener_id??'') })
    this._waitingFor.push({
      listeners,
      action,
      params,
    })
    this._checkWaitFor()
  } // waitFor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @description
  * This method is used internally to fire message when all dependencies were finished
  */
  _checkWaitFor() {
    if(SEQUENTIAL_START && starting_listener) return
    this._waitingFor = this._waitingFor.filter(waiter=>{
      waiter.listeners = waiter.listeners.filter(listener_id=>{
        return(
          !_listeners[listener_id] ||
        (_listeners[listener_id].status!=='started' && _listeners[listener_id].status!=='exited')
        )
      })
      if(waiter.listeners.length===0) {
        if(SEQUENTIAL_START) {
          if(starting_listener) {
            return true
          }else{
            starting_listener = this.listener_id
          }
        }

        mosy_loaded_dt = mosy_loaded_dt || unow()

        // send the action to me
        setTimeout(()=>{
          // this.debug('Invoked start of listener')
          this.listener_start_dt1 = unow()
          this.publish('#ME', waiter.action, waiter.params)
        }, SEQUENTIAL_START ? SEQUENTIAL_DELAY : 0)

        return false // remove from wait list
      }else {
        return true // keep in wait list
      }
    })
  } // _checkWaitFor

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @private
  * @description
  * This method is used internally to fire message if all listeners were started
  */
  _checkStartedAll() {
    // send mmStartedAll first time when all listeners are started resp. exited
    if(!mosy_started_dt && Object.keys(_listeners).filter((id)=>{ return _listeners[id].status !== 'started' && _listeners[id].status !== 'exited' }).length===0) {
      mosy_started_dt = unow()
      this.publish('#ALLWITHME', 'mmStartedAll', { init: MOSY_PROCESS_START_US, loaded: mosy_loaded_dt, started: mosy_started_dt })
    }
  } // _checkStartedAll

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Returns hash of listeners as pairs of listener_id:status
  * @param {string} status - optional; if set, method will return only listeners with given status
  */
  getListeners(status) {
    const listeners = {}
    for(const x in _listeners) {
      if(!status || _listeners[x].status===status) { listeners[x] = _listeners[x].status }
    }
    return listeners
  } // getListeners

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Returns hash of configuration of all listeners
  */
  getListenersConfiguration() {
    const cfg = {}
    for(const x in _listeners) {
      if(_listeners[x].ref) { cfg[x] = _.cloneDeep(_listeners[x].ref.config) }
    }
    return cfg
  } // getListenersConfiguration

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Log debug message.
  * @param {string} text - text to log
  */
  debug(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    logger.debug(this.listener_id, text)
    this.publish('#LOG', 'mmDebug', { text })
  } // debug

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Log info message.
  * @param {string} text - text to log
  */
  info(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    logger.info(this.listener_id, text)
    this.publish('#LOG', 'mmInfo', { text })
  } // info

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Log warning message.
  * @param {string} text - text to log
  */
  warn(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    logger.warn(this.listener_id, text)
    this.publish('#LOG', 'mmWarning', { text })
  } // warn

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Log error message.
  * @param {string} text - text to log
  */
  error(...args) {
    const text = args.map(a=>typeof (a)==='string'?a:JSON.stringifySafe(a)).join(' ')
    logger.error(this.listener_id, text)
    this.publish('#LOG', 'mmError', { text })
  } // debug

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Generates system-unique object identifactor as 10-chars long string containing 0-9a-zA-Z
  * OID has format: 6 bytes app_id, 1 byte OID format (in this case "0"), 9 bytes timestamp
  */
  oid() {
    if(!this.app_id) {
      this.error('Cannot generate OID without APPID')
      return
    }
    return this.app_id+mcrypt.oid()
  } // oid

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * msysDomainClient informs all listeners that it successfully identifies with given app_id
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {string} [params.app_id] - unique application ID
  * @param {string} [params.app_key] - private key of application
  * @param {string} [params.app_cert] - application certificate signed by deployment PKI CA
  * @param {string} [params.unit_id] - unique identifier for the logical patrolling unit
  */
  mmAppIdentified(sender, params) {
    this.app_id = params.app_id
    this.app_key = params.app_key
    this.app_cert = params.app_cert
    this.unit_id = params.unit_id
  } // mmAppIdentified

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Starts a daemon - a method to be run in defined interval.
  * It will be automatically stopped when mmExit comes.
  * If such daemon already exists it will be replaced.
  * `this.daemon(1000, this.myMethod)` - will run `this.myMethod()` every second
  * `this.daemon(1000, 'myMethod')` - the same
  * @param {number} interval - interval in which the daemon shall be called (in ms)
  * @param {function|string} daemon - function or name of method to be called
  * @param {boolean|integer} invoke_immediately - if true, daemon will also be invoked immediately; if positive value, will wait that miliseconds before the invocation
  */
  startDaemon(interval, daemon, invoke_immediately, long_duration = 100) {
    long_duration = _.natural(long_duration)
    this.stopDaemon(daemon)
    if(typeof (daemon)==='string') { daemon = this[daemon] }
    if(typeof (daemon)!=='function') {
      this.warn('Cannot set daemon')
      return
    }

    const wait_first = invoke_immediately > 0 ? invoke_immediately : 0
    return setTimeout(()=>{
      const intervaler = setInterval(()=>{
        const dt = Date.now()
        daemon.call(this)
        const duration = Date.now() - dt
        if(long_duration>0 && duration > long_duration) this.warn('Long daemon: '+String(daemon??'').replace(/[\n\r][\s\S]*/, ''), duration, 'ms')
      }, interval)
      this._daemons.push([daemon, intervaler])
      if(invoke_immediately) setTimeout(()=>{ daemon.call(this) }, 1)
    }, wait_first)
  } // startDaemon

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Manually stop daemon started with startDaemon()
  * @param {function|string} daemon - function or name of method; same used in startDaemon
  */
  stopDaemon(daemon) {
    if(typeof (daemon)==='string') { daemon = this[daemon] }
    if(typeof (daemon)!=='function') return this.warn('Cannot stop daemon')
    for(let i=0; i<this._daemons.length; i++) {
      const daemon_def = this._daemons[i]
      if(daemon_def[0]===daemon) {
        clearInterval(daemon_def[1])
        this._daemons.splice(i, 1)
      }
    }
  } // stopDaemon

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Manually stop daemon started with startDaemon()
  * @param {function|string} daemon - function or name of method; same used in startDaemon
  */
  stopwatch(name, watch_id) {
    const dt = unow()
    watch_id = watch_id || 'run'
    const previous_dt = this._stopwatch[watch_id]
    this._stopwatch[watch_id] = dt
    if(previous_dt && name) this.debug(`stopwatch ${watch_id}:${name} after ${((dt-previous_dt)/1000).toFixed(3)} us`)
  } // stopDaemon

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Clones action parameters using either JSON methods or _.cloneDeep if necessary
  * @param {string} action - action to be performed
  * @param {object} params - action parameters to be duplicated
  */
  cloneParams(action, params) {
    if(action==='mmH264') return { ...params } // for mmH264, do just shallow copy to prevent working with large payload
    return ms.cloneParams(params)
  } // cloneParams

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * retrieves list of messages recorded on disk
  * @param {integer} start_us - start time
  * @param {integer} end_us - end time
  * @param {string|array} filter - filter which messages to get
  * @param {string|array} index - only messages in seconds where any of index(es) match will be returned
  * @param {integer} [timeout] - timeout for the search in ms
  */
  async getMessages(start_us, end_us, filter, index, timeout) {
    timeout = parseInt(timeout)
    if(!(timeout>0)) timeout = 10_000

    let listener_id = null
    for(let i=1; i<1000; i++) {
      const candidate_id = this.listener_id+'_getmessages_'+i
      if(!_listeners[candidate_id]) {
        listener_id = candidate_id
        break
      }
    }

    if(!listener_id) listener_id = 0 // fallback to 0 that will automatically generate numeric listener ID

    // eslint-disable-next-line new-cap
    const reader = new msysListener(listener_id)
    const messages = []
    reader.mmMessage = (_, params)=>{
      if(params.action) {
        delete params.event_dt // remove mmMessage's own time
        messages.push(params)
      }
    }
    await asyncTimeout(1)

    try{
      const params = {
        start: start_us,
        end: end_us,
        delay: 1,
        filter,
        index,
      }
      await reader.request('SERVER@msysStreamer', 'mmPlay', params, { timeout, resolve: 'mmSuccess' })
      reader.mmExit({}, { delete: true })
      return messages
    }catch(e) {
      reader.error('getMessages error: '+e)
      reader.mmExit({}, { delete: true })
      return []
    }
  } // getMessages

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * finds one message recorded on disk
  * @param {integer} start_us - start time
  * @param {integer} end_us - end time
  * @param {string} [direction] - whether to search forward or backward; default is forward
  * @param {string} [action] - name of action to be searched
  * @param {string|array} [filter] - filter that must be matched
  * @param {string|array} [index] - only messages in seconds where any of index(es) match will be returned
  * @param {integer} [timeout] - timeout for the search in ms
  */
  async findMessage(start_us, end_us, direction, action, filter, index, timeout) {
    const start = parseInt(start_us)
    if(!(start>0)) {
      this.error('findMessage invalid start_us: '+start_us)
      return
    }

    const end = parseInt(end_us)
    if(!(end>0)) {
      this.error('findMessage invalid end_us: '+end_us)
      return
    }

    direction = /back/i.test(direction) ? 'backward' : /forward|fwd/i.test(direction) ? 'forward' : direction
    if(direction!=='forward' && direction!=='backward') {
      this.error('findMessage invalid direction: '+direction)
      return
    }
    action = String(action??'')
    filter = Array.isArray(filter) ? filter : String(filter??'')
    index = String(index??'')
    timeout = parseInt(timeout)
    if(timeout<0) {
      this.error('findMessage invalid timeout: '+timeout)
      return
    }

    timeout = timeout > 0 ? timeout : 10_000

    const request = {
      start,
      end,
      direction,
    }
    if(action.length) request.action = action
    if(filter.length>0) request.filter = filter
    if(index.length>0) request.index = index

    try{
      const reply = await this.request('SERVER@msysStreamer', 'mmFindMessage', request)

      const invalid = (
        !reply ||
        typeof reply !== 'object' ||
        reply.action !== 'mmMessage' ||
        !reply.params ||
        typeof reply.params !== 'object' ||
        typeof reply.params.sender !== 'object' ||
        typeof reply.params.sender.listener_id !== 'string' ||
        reply.params.sender.listener_id === '' ||
        typeof reply.params.target !== 'object' ||
        typeof reply.params.target.listener_id !== 'string' ||
        reply.params.target.listener_id === '' ||
        typeof reply.params.action !== 'string' ||
        reply.params.action === '' ||
        (reply.params.params && typeof reply.params.params !== 'object')
      )

      if(!invalid) {
        // ensure only four core message parameters are returned
        // this prevents returning e.g. reply.params.event_dt of mmMessage itself
        let { sender, target, action, params } = reply.params
        if(!params) params = {}
        const msg = { sender, target, action, params }
        return msg
      }else if(reply?.action !== 'mmFailure') {
        this.error('findMessage returned invalid message')
      }
    }catch(e) {
      this.error('findMessage error: '+e)
    }
  } // findMessage

  maskConfiguration(cfg) {
    try{
      const seen = new WeakSet()
      const re = /(\b|_)pass|app_key|app_cert|(\b|_)cert|privatekey|radar_key/i
      const str = JSON.stringify(cfg, (key, val) => {
        if(typeof val === 'object' && val !== null) {
          if(seen.has(val)) return
          seen.add(val)
        }
        if(re.test(key)) {
          if(typeof val==='string' && val!=='') return '…'
        }
        return val
      })
      return JSON.parse(str)
    }catch(e) {
      this.warn('Problem masking configuration: '+e)
    }
  } // maskConfiguration

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Request to get actual configuration of the listener
  * @param {object} sender - message sender
  * @param {object} params - message parameters
  * @param {boolean} [params.no_mask] - allows return of unmasked passwords for msysExec
  */
  mmGetConfig(sender, params) {
    let config
    if(params.no_mask && this.address2string(sender)==='msysExec') {
      config = JSON.parse(JSON.stringifySafe(this.config))
    }else{
      config = this.maskConfiguration(this.config)
    }

    this.reply(sender, 'mmConfig', { config })
  } // mmGetConfig

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * reorders parameters for action to comply with order as defined in message catalogue
  * @param {object} action_or_type - message action or message type
  * @param {object} params - message parameters
  */
  reorderParams(action_or_type, params) {
    if(!params || typeof params!=='object') return params

    // construct ordered list of defined parameter names
    const properties = schema_actions[action_or_type]?.properties || schema_types[action_or_type]?.properties
    if(!properties || typeof properties!=='object') return params
    const desired = Object.keys(properties)
    desired.unshift('orig_dt')
    desired.unshift('event_dt')
    desired.push('_meta')

    // calculate ordering value for each key
    let ordering = Object.keys(params).map(key=>[key, desired.indexOf(key)<0 ? Infinity : desired.indexOf(key)])
    ordering = ordering.sort((a, b)=>a[1]-b[1])
    const out = {}
    for(const [key] of ordering) out[key] = params[key]
    return out
  } // reorderParams

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Obtains listener-scoped lock.
  * Used to make a function call exclusive, e.g.
  * async someFunction() {
  *   await lock("run")
  *   try {
  *     ...
  *   } finally {
  *     unlock("run")
  *   }
  * }
  * @param {string} id - lock ID
  */
  lock(id) {
    if(!this._locks) this._locks = {}
    id = String(id??'')
    let resolve
    const promise = new Promise(_resolve => {
      resolve=_resolve
    })
    if(!this._locks[id]) this._locks[id] = []
    this._locks[id].push(resolve)
    if(this._locks[id].length === 1) resolve() // nobody else in queue, we can immediately run
    return promise
  } // lock

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Release listener-scoped lock
  * @param {string} id - lock ID
  */
  unlock(id) {
    if(!this._locks) return
    id = String(id??'')
    if(!this._locks[id]) return
    this._locks[id].shift()
    if(this._locks[id].length>0) {
      this._locks[id][0]()
    }else {
      delete this._locks[id]
    }
  } // unlock

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Obtains application-scoped lock.
  * Used to make a function call exclusive, e.g.
  * async someFunction() {
  *   await lock("run")
  *   try {
  *     ...
  *   } finally {
  *     unlock("run")
  *   }
  * }
  * @param {string} id - lock ID
  */
  globalLock(id) {
    id = String(id??'')
    let resolve
    const promise = new Promise(_resolve => {
      resolve = _resolve
    })
    if(!_locks[id]) _locks[id] = []
    _locks[id].push(resolve)
    if(_locks[id].length === 1) resolve() // nobody else in queue, we can immediately run
    return promise
  } // globalLock

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Release application-scoped lock
  * @param {string} id - lock ID
  */
  globalUnlock(id) {
    if(!_locks) return
    id = String(id??'')
    if(!_locks[id]) return
    _locks[id].shift()
    if(_locks[id].length>0) {
      _locks[id][0]()
    }else {
      delete _locks[id]
    }
  } // globalUnlock

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Creates helper function used to verify if there was not another call to method after async operation.
  * Used to abort if another call to function/method was made during async operation.
  * Example usage:
  * async someFunction() {
  *   const abort = this.aborter("someFunction")
  *   await someAsyncOperation()
  *   if(!abort()) return // another call was made during async operation, aborting
  */
  aborter(id) {
    if(!this._aborts) this._aborts = {}
    id = String(id??'')
    const seq = (this._aborts[id] ?? 0) + 1
    this._aborts[id] = seq
    return () => this._aborts[id] !== seq
  } // aborter

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * @description
  * Creates helper function used to verify if there was not another call to method after async operation.
  * Used to abort if another call to function/method was made during async operation.
  * Example usage:
  * async someFunction() {
  *   const abort = this.aborter("someFunction")
  *   await someAsyncOperation()
  *   if(!abort()) return // another call was made during async operation, aborting
  */
  globalAborter(id) {
    id = String(id??'')
    const dt = Date.now()
    _aborts[id] = dt
    return () => _aborts[id] !== dt
  } // globalAborter
} // msysListener
